import { SupportedLanguages } from "@/enums/internationalization";
import { VoiceOption } from "@/types/agencies";

const LANGUAGE_PRIORITY: SupportedLanguages[] = [
  SupportedLanguages.English,
  SupportedLanguages.Spanish,
  SupportedLanguages.Portuguese,
  SupportedLanguages.French,
  SupportedLanguages.German,
  SupportedLanguages.Japanese,
];

const CANONICAL_LANGUAGE_ORDER: SupportedLanguages[] = (() => {
  const seen = new Set<SupportedLanguages>();
  const ordered: SupportedLanguages[] = [];
  for (const lang of [...LANGUAGE_PRIORITY, ...Object.values(SupportedLanguages)]) {
    if (seen.has(lang)) continue;
    seen.add(lang);
    ordered.push(lang);
  }
  return ordered;
})();

export type VoiceLanguageGroup = {
  key: string;
  languages: SupportedLanguages[];
  voices: VoiceOption[];
  isEnglishSpanish: boolean;
};

export function canonicalizeLanguages(
  languages: readonly SupportedLanguages[] | undefined
): SupportedLanguages[] {
  const present = new Set(languages ?? []);
  return CANONICAL_LANGUAGE_ORDER.filter((lang) => present.has(lang));
}

/** Org language chips that actually have at least one voice in the catalog. */
export function orgLanguageChipsForCatalog(
  orgLanguages: readonly SupportedLanguages[] | undefined,
  catalog: readonly Pick<VoiceOption, "supportedLanguages">[]
): SupportedLanguages[] {
  const present = new Set<SupportedLanguages>();
  for (const voice of catalog) {
    for (const lang of voice.supportedLanguages ?? []) {
      present.add(lang);
    }
  }
  return canonicalizeLanguages(orgLanguages).filter((lang) => present.has(lang));
}

export function languageGroupKey(languages: readonly SupportedLanguages[]): string {
  return languages.length > 0 ? languages.join("&") : "other";
}

function groupSortRank(languages: SupportedLanguages[]): number {
  const hasEnglish = languages.includes(SupportedLanguages.English);
  const hasSpanish = languages.includes(SupportedLanguages.Spanish);

  if (languages.length === 1 && hasEnglish) return 0;
  if (languages.length === 2 && hasEnglish && hasSpanish) return 1;
  if (languages.length === 1 && hasSpanish) return 2;
  if (hasEnglish) return 10;
  if (hasSpanish) return 20;
  return 100;
}

function isEnglishSpanish(languages: SupportedLanguages[]): boolean {
  return (
    languages.length === 2 &&
    languages.includes(SupportedLanguages.English) &&
    languages.includes(SupportedLanguages.Spanish)
  );
}

/**
 * Group voices by spoken language for the Explore catalog. When the org has
 * languages configured, headings are scoped to those languages so an
 * English + Spanish + Catalan voice sits under "English & Spanish" instead of
 * fragmenting into its own "English & Spanish & Catalan" section.
 */
export function groupVoicesByLanguage(
  voices: readonly VoiceOption[],
  orgLanguages: readonly SupportedLanguages[] = []
): VoiceLanguageGroup[] {
  const buckets = new Map<string, VoiceLanguageGroup>();
  const orgLanguageSet = new Set(orgLanguages);

  for (const voice of voices) {
    const spoken = voice.supportedLanguages ?? [];
    const scoped =
      orgLanguageSet.size > 0 ? spoken.filter((lang) => orgLanguageSet.has(lang)) : spoken;
    // A voice that speaks none of the org languages (e.g. a pinned legacy
    // selection) keeps its real languages rather than landing in "Other".
    const languages = canonicalizeLanguages(scoped.length > 0 ? scoped : spoken);
    const key = languageGroupKey(languages);
    const existing = buckets.get(key);
    if (existing) {
      existing.voices.push(voice);
      continue;
    }
    buckets.set(key, {
      key,
      languages,
      voices: [voice],
      isEnglishSpanish: isEnglishSpanish(languages),
    });
  }

  return [...buckets.values()].sort((a, b) => {
    const rankDiff = groupSortRank(a.languages) - groupSortRank(b.languages);
    if (rankDiff !== 0) return rankDiff;
    return a.key.localeCompare(b.key);
  });
}
